/**
 * Create/Edit Identity Step
 *
 * Second step in billing setup wizard - creates or edits a billing identity.
 * User enters NPI, Tax ID, legal name, taxonomy code, and billing address.
 * Supports editing an existing entity when coming back from failed verification.
 */

import { useEffect, useState } from 'react';
import {
  type BillingEntityAddressFormData,
  BillingEntityForm,
  type BillingEntityFormData,
  type CreateBillingEntityParams,
  initialBillingEntityFormData,
  type PayToToggleSource,
  useBillingEntity,
  useBillingEntityOperations,
  validatePayToAddress,
} from '@features/billing-entities';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { extractApiErrorMessage } from '../../utils/errorUtils';

interface CreateEntityStepProps {
  onNext: () => void;
  onBack: () => void;
  onEntityCreated: (entityId: string) => void;
  /** Optional existing entity ID for editing mode */
  existingEntityId?: string | null;
}

export function CreateEntityStep({ onNext, onBack, onEntityCreated, existingEntityId }: CreateEntityStepProps) {
  const [formData, setFormData] = useState<BillingEntityFormData>(initialBillingEntityFormData);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { billing } = useEventTracking();

  // Fetch existing entity if we have an ID
  const { entity: existingEntity, isLoading: entityLoading } = useBillingEntity(existingEntityId || null);

  const { create, createLoading, createError, createSuccess, update, updateLoading } = useBillingEntityOperations();
  const [createdEntityId, setCreatedEntityId] = useState<string | null>(existingEntityId || null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Pre-fill form with existing entity data
  useEffect(() => {
    if (existingEntity && !formInitialized) {
      const payToAddress = existingEntity.pay_to_address;
      setFormData({
        billing_npi: existingEntity.billing_npi || '',
        tax_id: '', // Can't pre-fill - it's masked
        tax_id_type: existingEntity.tax_id_type || 'EIN',
        taxonomy_code: existingEntity.taxonomy_code || '',
        legal_name: existingEntity.legal_name || '',
        doing_business_as: existingEntity.doing_business_as || '',
        billing_address: {
          line1: existingEntity.billing_address?.line1 || '',
          line2: existingEntity.billing_address?.line2 || '',
          city: existingEntity.billing_address?.city || '',
          state: existingEntity.billing_address?.state || '',
          zip: existingEntity.billing_address?.zip || '',
        },
        pay_to_address: {
          line1: payToAddress?.line1 || '',
          line2: payToAddress?.line2 || '',
          city: payToAddress?.city || '',
          state: payToAddress?.state || '',
          zip: payToAddress?.zip || '',
        },
        has_pay_to_address: !!payToAddress,
        contact_name: existingEntity.contact_name || '',
        contact_email: existingEntity.contact_email || '',
        contact_phone: existingEntity.contact_phone || '',
      });
      setIsEditMode(true);
      setFormInitialized(true);
    }
  }, [existingEntity, formInitialized]);

  const handleChange = (field: keyof BillingEntityFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleAddressChange = (field: keyof BillingEntityAddressFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      billing_address: { ...prev.billing_address, [field]: value },
    }));
  };

  const handlePayToAddressChange = (field: keyof BillingEntityAddressFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      pay_to_address: { ...prev.pay_to_address, [field]: value },
    }));
    // Clear the matching per-field error as the user corrects it.
    setErrors(prev => {
      const key = `pay_to_address.${field}`;
      if (!prev[key]) return prev;
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleTogglePayTo = (checked: boolean, source: PayToToggleSource) => {
    setFormData(prev => ({ ...prev, has_pay_to_address: checked }));
    if (!checked) {
      // Drop any stale pay-to validation errors along with the fields.
      setErrors(prev =>
        Object.fromEntries(Object.entries(prev).filter(([key]) => !key.startsWith('pay_to_address.'))),
      );
    }
    billing.payToAddressToggled({ enabled: checked, source, context: 'wizard' });
    if (source === 'po_box_warning') {
      billing.poBoxWarningActionClicked({ context: 'wizard' });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<string, string>> = {};

    if (!formData.billing_npi.trim() || !/^\d{10}$/.test(formData.billing_npi)) {
      newErrors.billing_npi = 'NPI must be exactly 10 digits';
    }

    // Tax ID validation - only required for new entities or if user is changing it
    if (!isEditMode) {
      if (!formData.tax_id.trim()) {
        newErrors.tax_id = 'Tax ID is required';
      } else if (formData.tax_id_type === 'EIN' && !/^\d{2}-?\d{7}$/.test(formData.tax_id)) {
        newErrors.tax_id = 'EIN must be in format XX-XXXXXXX or XXXXXXXXX';
      } else if (formData.tax_id_type === 'SSN' && !/^\d{3}-?\d{2}-?\d{4}$/.test(formData.tax_id)) {
        newErrors.tax_id = 'SSN must be in format XXX-XX-XXXX or XXXXXXXXX';
      }
    } else if (formData.tax_id.trim()) {
      // If editing and user provided a tax_id, validate it
      if (formData.tax_id_type === 'EIN' && !/^\d{2}-?\d{7}$/.test(formData.tax_id)) {
        newErrors.tax_id = 'EIN must be in format XX-XXXXXXX or XXXXXXXXX';
      } else if (formData.tax_id_type === 'SSN' && !/^\d{3}-?\d{2}-?\d{4}$/.test(formData.tax_id)) {
        newErrors.tax_id = 'SSN must be in format XXX-XX-XXXX or XXXXXXXXX';
      }
    }

    if (!formData.legal_name.trim()) {
      newErrors.legal_name = 'Legal name is required';
    }

    if (!formData.billing_address.line1.trim()) {
      newErrors.billing_address = 'Address line 1 is required';
    }

    if (!formData.billing_address.city.trim()) {
      newErrors.billing_address = 'City is required';
    }

    if (!formData.billing_address.state.trim() || formData.billing_address.state.length !== 2) {
      newErrors.billing_address = 'Valid 2-letter state code is required';
    }

    if (!formData.billing_address.zip.trim() || !/^\d{5}(-\d{4})?$/.test(formData.billing_address.zip)) {
      newErrors.billing_address = 'Valid ZIP code is required (XXXXX or XXXXX-XXXX)';
    }

    // Pay-to address: only validated when the provider opted in. Per-field
    // error keys (not the single-string billing_address wart above) so each
    // input can surface its own message.
    if (formData.has_pay_to_address) {
      Object.assign(newErrors, validatePayToAddress(formData.pay_to_address));
    }

    // Contact info is required
    if (!formData.contact_email.trim()) {
      newErrors.contact_email = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = 'Please enter a valid email address';
    }

    if (!formData.contact_phone.trim()) {
      newErrors.contact_phone = 'Contact phone is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitError(null);

    // Trimmed 2010AB payload, or null when the provider hasn't opted in.
    const payToAddressPayload = formData.has_pay_to_address
      ? {
          line1: formData.pay_to_address.line1.trim(),
          line2: formData.pay_to_address.line2.trim() || null,
          city: formData.pay_to_address.city.trim(),
          state: formData.pay_to_address.state.trim().toUpperCase(),
          zip: formData.pay_to_address.zip.trim(),
        }
      : null;

    try {
      if (isEditMode && existingEntityId) {
        // Update existing entity
        const updatePayload: Record<string, unknown> = {
          billing_npi: formData.billing_npi.replace(/\D/g, ''),
          taxonomy_code: formData.taxonomy_code.trim() || null,
          legal_name: formData.legal_name.trim(),
          doing_business_as: formData.doing_business_as.trim() || null,
          billing_address: {
            line1: formData.billing_address.line1.trim(),
            line2: formData.billing_address.line2.trim() || null,
            city: formData.billing_address.city.trim(),
            state: formData.billing_address.state.trim().toUpperCase(),
            zip: formData.billing_address.zip.trim(),
          },
          contact_name: formData.contact_name.trim() || null,
          contact_email: formData.contact_email.trim(),
          contact_phone: formData.contact_phone.trim(),
        };

        // pay_to_address and clear_pay_to_address are mutually exclusive —
        // sending both is a 400.
        if (payToAddressPayload) {
          updatePayload.pay_to_address = payToAddressPayload;
        } else if (existingEntity?.pay_to_address) {
          updatePayload.clear_pay_to_address = true;
        }

        // Only include tax_id if user explicitly provided one (editing)
        if (formData.tax_id.trim()) {
          updatePayload.tax_id = formData.tax_id.replace(/\D/g, '');
          updatePayload.tax_id_type = formData.tax_id_type;
        }

        await update({
          billingEntityId: existingEntityId,
          updateBillingEntityRequest: updatePayload,
        }).unwrap();

        setCreatedEntityId(existingEntityId);
        onEntityCreated(existingEntityId);
        enqueueSnackbar('Billing identity updated! Try verifying again.', { variant: 'success' });
      } else {
        // Create new entity
        const createPayload: CreateBillingEntityParams = {
          billing_npi: formData.billing_npi.replace(/\D/g, ''),
          tax_id: formData.tax_id.replace(/\D/g, ''),
          tax_id_type: formData.tax_id_type,
          taxonomy_code: formData.taxonomy_code.trim() || null,
          legal_name: formData.legal_name.trim(),
          doing_business_as: formData.doing_business_as.trim() || null,
          billing_address: {
            line1: formData.billing_address.line1.trim(),
            line2: formData.billing_address.line2.trim() || null,
            city: formData.billing_address.city.trim(),
            state: formData.billing_address.state.trim().toUpperCase(),
            zip: formData.billing_address.zip.trim(),
          },
          pay_to_address: payToAddressPayload,
          contact_name: formData.contact_name.trim() || null,
          contact_email: formData.contact_email.trim(),
          contact_phone: formData.contact_phone.trim(),
          is_default: true,
        };

        const result = await create({
          createBillingEntityRequest: createPayload,
        }).unwrap();

        if (result && 'billing_entity_id' in result) {
          setCreatedEntityId(result.billing_entity_id);
          onEntityCreated(result.billing_entity_id);
          enqueueSnackbar('Billing identity added!', { variant: 'success' });
        }
      }
    } catch (err) {
      const errorMessage = extractApiErrorMessage(
        err,
        `Failed to ${isEditMode ? 'update' : 'add'} billing identity. Please check your information and try again.`,
      );
      setSubmitError(errorMessage);
      // The backend rejects PO Box / PMB style billing addresses with a
      // validation_error naming "physical street address". Pin it to the
      // billing address so the recovery is obvious instead of a bare toast.
      if (/physical street address/i.test(errorMessage)) {
        setErrors(prev => ({ ...prev, billing_address: errorMessage }));
      }
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} billing identity:`, err);
    }
  };

  const isSubmitting = createLoading || updateLoading;

  const handleContinue = () => {
    if (createdEntityId) {
      onNext();
    }
  };

  // Loading state for existing entity
  if (existingEntityId && entityLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if ((createSuccess || (isEditMode && createdEntityId)) && createdEntityId) {
    return (
      <Box>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {isEditMode ? 'Billing identity updated!' : 'Billing identity added!'}
          </Typography>
          <Typography variant="body2">
            Next, we&apos;ll verify your information with the clearinghouse.
          </Typography>
        </Alert>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={onBack} fullWidth>
            Back
          </Button>
          <Button variant="contained" onClick={handleContinue} fullWidth>
            Continue to Verify
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {isEditMode ? 'Edit Billing Identity' : 'Add Billing Identity'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} component="p">
        {isEditMode
          ? 'Update your billing information and try verifying again.'
          : 'Your billing identity is the NPI and Tax ID that appears on claims, identifying your practice to insurance companies.'}
      </Typography>

      {isEditMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Fix any issues below and save to retry verification. Current Tax ID is masked for security - leave blank to keep existing.
        </Alert>
      )}

      <Box component="form" sx={{ mt: 3 }}>
        <BillingEntityForm
          data={formData}
          onChange={handleChange}
          onAddressChange={handleAddressChange}
          onPayToAddressChange={handlePayToAddressChange}
          onTogglePayTo={handleTogglePayTo}
          errors={errors}
          taxIdRequired={!isEditMode}
          taxIdHelperText={
            isEditMode
              ? `Current: ${existingEntity?.tax_id_masked || 'unknown'} - Leave blank to keep, or enter new value`
              : undefined
          }
          taxIdPlaceholder={isEditMode ? 'Leave blank to keep current' : undefined}
        />

        {createError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {createError && typeof createError === 'object' && 'data' in createError && createError.data && typeof createError.data === 'object' && 'message' in createError.data
              ? String(createError.data.message)
              : 'Failed to create billing entity. Please check your information and try again.'}
          </Alert>
        )}

        {errors.billing_address && (
          <FormHelperText error sx={{ mt: 2 }}>
            {errors.billing_address}
          </FormHelperText>
        )}

        {submitError && !createError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onBack} disabled={isSubmitting} fullWidth>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            fullWidth
            startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
          >
            {isSubmitting
              ? isEditMode
                ? 'Saving...'
                : 'Adding...'
              : isEditMode
                ? 'Save & Retry Verification'
                : 'Add Billing Identity'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
